import { useState } from 'react';

// Software categories closely mirroring our daily toolchain
export const SOFTWARE_CATEGORIES = {
  'CAD & Drafting': {
    tagline: 'Industry-standard 2D/3D CAD platforms our drafters work in daily.',
    items: [
      { name: 'AutoCAD', vendor: 'Autodesk' },
      { name: 'AutoCAD LT', vendor: 'Autodesk' },
      { name: 'Civil 3D', vendor: 'Autodesk' },
      { name: 'BricsCAD', vendor: 'Bricsys' },
      { name: 'ProgeCAD', vendor: 'ProgeSOFT' },
      { name: 'IntelliCAD', vendor: 'ITC' },
      { name: 'Carlson Survey', vendor: 'Carlson' },
      { name: 'MicroStation', vendor: 'Bentley' },
      { name: 'DraftSight', vendor: 'Dassault' },
      { name: 'ZWCAD', vendor: 'ZWSOFT' },
    ],
  },
  'BIM Software': {
    tagline: 'BIM authoring and coordination for multidiscipline project delivery.',
    items: [
      { name: 'Revit', vendor: 'Autodesk' },
      { name: 'Navisworks', vendor: 'Autodesk' },
      { name: 'BIM 360 / ACC', vendor: 'Autodesk' },
      { name: 'OpenBuildings', vendor: 'Bentley' },
      { name: 'Tekla Structures', vendor: 'Trimble' },
      { name: 'ArchiCAD', vendor: 'Graphisoft' },
    ],
  },
  'GIS & Survey': {
    tagline: 'GIS, geodetic adjustment and field-to-finish survey platforms.',
    items: [
      { name: 'ArcGIS Pro', vendor: 'Esri' },
      { name: 'ArcGIS Online', vendor: 'Esri' },
      { name: 'QGIS', vendor: 'OSGeo' },
      { name: 'Terramodel', vendor: 'Trimble' },
      { name: 'Trimble Business Center', vendor: 'Trimble' },
      { name: 'Leica Infinity', vendor: 'Leica' },
      { name: 'Carlson SurvCE / SurvPC', vendor: 'Carlson' },
      { name: 'StarNet Adjustment', vendor: 'Microsurvey' },
      { name: 'Global Mapper', vendor: 'Blue Marble' },
    ],
  },
  '3D Modeling & Visualization': {
    tagline: 'Point-cloud, photogrammetry, and visualization tools for 3D deliverables.',
    items: [
      { name: 'ReCap Pro', vendor: 'Autodesk' },
      { name: 'Pix4D', vendor: 'Pix4D' },
      { name: 'Agisoft Metashape', vendor: 'Agisoft' },
      { name: 'CloudCompare', vendor: 'OSGeo' },
      { name: 'SketchUp', vendor: 'Trimble' },
      { name: 'Blender', vendor: 'Blender Foundation' },
      { name: 'Lumion', vendor: 'Lumion' },
    ],
  },
};

export function SoftwareShowcase() {
  const tabs = Object.keys(SOFTWARE_CATEGORIES);
  const [active, setActive] = useState(tabs[0]);
  const data = SOFTWARE_CATEGORIES[active];

  return (
    <section className="section-padding bg-white" data-testid="software-showcase">
      <div className="container-custom">
        <div className="text-center mb-10 reveal">
          <span className="label-mono text-[#06D6E0] mb-4 block">Technology Stack</span>
          <h2 className="heading-2">Software We Use</h2>
          <p className="text-zinc-600 mt-3 max-w-2xl mx-auto">
            From AutoCAD and Revit to ArcGIS and Terramodel — our team is fluent across 30+ professional platforms.
          </p>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap justify-center gap-2 mb-8 reveal delay-100">
          {tabs.map(t => (
            <button
              key={t}
              onClick={() => setActive(t)}
              data-testid={`software-tab-${t.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              className={`px-5 py-2.5 text-sm font-medium rounded-full transition-colors ${
                active === t
                  ? 'bg-[#0A0A23] text-white'
                  : 'bg-zinc-100 text-zinc-700 hover:bg-zinc-200'
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        <p className="text-center text-sm text-zinc-500 mb-8">{data.tagline}</p>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 reveal delay-200">
          {data.items.map((sw) => (
            <div
              key={sw.name}
              className="bg-[#F7F7F3] border border-zinc-200 rounded-lg px-4 py-5 text-center hover:border-[#3B82F6] hover:shadow-md transition-all"
              data-testid={`software-item-${sw.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
            >
              <div className="w-10 h-10 mx-auto mb-3 rounded bg-gradient-to-br from-[#3B82F6] to-[#8B5CF6] text-white flex items-center justify-center font-black text-sm" style={{ fontFamily: 'Outfit' }}>
                {sw.name.split(' ').map(w => w[0]).slice(0, 2).join('')}
              </div>
              <p className="font-bold text-zinc-900 text-sm leading-tight" style={{ fontFamily: 'Outfit' }}>{sw.name}</p>
              <p className="text-[11px] text-zinc-500 mt-1">{sw.vendor}</p>
            </div>
          ))}
        </div>

        <div className="mt-10 text-center reveal delay-300">
          <div className="inline-flex items-center gap-3 bg-[#0A0A23] text-white px-6 py-4 rounded-md">
            <span className="text-[#06D6E0] font-mono text-sm font-bold">LISP</span>
            <p className="text-sm text-zinc-300">
              We are fluent in AutoCAD LISP; custom scripts and routines accelerate every project.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
